import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import { Button } from '@react/components';
import { formatDate } from '@/features/document/logic';
import { documentTarget } from './complianceLogic';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

type Step = 'read' | 'confirm' | 'done';

export interface AcknowledgmentCeremonyProps {
  documentId: string;
  productId: string;
  documentTitle: string;
  acknowledgment: any;
  onCompleted: () => void;
  onCancel: () => void;
}

export const AcknowledgmentCeremony = observer(({
  documentId,
  productId,
  documentTitle,
  acknowledgment,
  onCompleted,
  onCancel,
}: AcknowledgmentCeremonyProps) => {
  const { t, locale } = useI18n();
  const documentStore = useDocument();
  const panelRef = useRef<HTMLDivElement>(null);
  const busyRef = useRef(false);
  const [step, setStep] = useState<Step>('read');
  const [documentOpened, setDocumentOpened] = useState(false);
  const [readConfirmed, setReadConfirmed] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState(false);
  const dueDate = acknowledgment?.due_date as string | undefined;
  const version = Number(acknowledgment?.document_version ?? 0);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    return () => previouslyFocused?.focus?.();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && step !== 'done' && !isBusy) {
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [step, isBusy, onCancel]);

  const confirmAcknowledgment = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setIsBusy(true);
    setError(false);
    try {
      await documentStore.AcknowledgeDocument('document', documentId, {
        target: documentTarget(documentId, productId),
        comment: t('document.ceremony.confirmComment'),
        document_version: version || undefined,
      });
      setStep('done');
    } catch {
      setError(true);
    } finally {
      busyRef.current = false;
      setIsBusy(false);
    }
  }, [documentStore, documentId, productId, t, version]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="acknowledgment-ceremony-title"
        className="relative flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col rounded-lg border border-border bg-popover"
      >
        <div className="border-b border-border px-5 py-4">
          <h2 id="acknowledgment-ceremony-title" className="text-sm font-semibold text-foreground">
            {t('document.ceremony.title')}
          </h2>
          {step !== 'done' && (
            <ol className="mt-3 flex items-center gap-2" aria-label={t('document.ceremony.progress')}>
              {[
                { key: 'read', label: t('document.ceremony.stepRead') },
                { key: 'confirm', label: t('document.ceremony.stepConfirm') },
              ].map((item, index) => {
                const currentIndex = step === 'read' ? 0 : 1;
                return (
                  <li key={item.key} className="flex flex-1 items-center gap-2" aria-current={index === currentIndex ? 'step' : undefined}>
                    <span className={`grid h-5 w-5 flex-shrink-0 place-items-center rounded-full border text-[10px] tabular-nums ${
                      index === currentIndex ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground'
                    }`}
                    >
                      {index + 1}
                    </span>
                    <span className={`truncate text-xs ${index === currentIndex ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                      {item.label}
                    </span>
                    {index === 0 && <span className="h-px flex-1 bg-border" aria-hidden="true" />}
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {step === 'done' ? (
          <div className="flex flex-col items-center px-6 py-10 text-center">
            <span className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-success/10 text-success">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3" />
              </svg>
            </span>
            <h3 className="text-sm font-semibold text-foreground">{t('document.ceremony.doneTitle')}</h3>
            <p className="mt-1.5 max-w-xs text-xs text-muted-foreground">{t('document.ceremony.doneDescription')}</p>
            <p className="mt-3 max-w-full truncate text-xs font-medium text-foreground">{documentTitle}</p>
            <Button size="sm" className="mt-6" onClick={onCompleted}>{t('common.done')}</Button>
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              {step === 'read' ? (
                <div className="space-y-4">
                  <div className="rounded-lg border border-border">
                    <div className="border-b border-border px-4 py-2.5">
                      <p className="text-[13px] font-medium text-foreground">{documentTitle}</p>
                      <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
                        {version > 0 && <span className="tabular-nums">{t('document.ackVersion')} {version}</span>}
                        {dueDate && (
                          <span className="tabular-nums">
                            {t('document.ceremony.dueBy', { date: formatDate(dueDate, locale) })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="px-4 py-3">
                      <p className="text-xs leading-relaxed text-muted-foreground">{t('document.ceremony.readHint')}</p>
                      <a
                        href={`/documents/${documentId}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => {
                          setDocumentOpened(true);
                          setReadConfirmed(false);
                        }}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                      >
                        {t('document.ceremony.openDocument')}
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                          <path d="M7 17 17 7M7 7h10v10" />
                        </svg>
                      </a>
                    </div>
                  </div>
                  <label className={`flex items-start gap-2.5 rounded-lg border border-border px-4 py-3 ${
                    documentOpened ? 'cursor-pointer hover:bg-muted/40' : 'opacity-60'
                  }`}
                  >
                    <input
                      type="checkbox"
                      checked={readConfirmed}
                      disabled={!documentOpened}
                      onChange={(event) => setReadConfirmed(event.target.checked)}
                      className="mt-0.5 accent-primary"
                    />
                    <span className="text-[13px] text-foreground">
                      {documentOpened ? t('document.ceremony.readConfirm') : t('document.ceremony.openFirst')}
                    </span>
                  </label>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-lg border border-border px-4 py-3">
                    <span className="text-xs text-muted-foreground">{t('document.ceremony.documentLabel')}</span>
                    <p className="mt-1 text-[13px] font-medium text-foreground">{documentTitle}</p>
                    {version > 0 && <p className="mt-1 text-xs tabular-nums text-muted-foreground">{t('document.ackVersion')} {version}</p>}
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">{t('document.ceremony.confirmDescription')}</p>
                  {error && <p role="alert" className="text-sm text-destructive">{t('document.ceremony.error')}</p>}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t border-border px-5 py-3.5">
              <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isBusy}>
                {t('common.cancel')}
              </Button>
              {step === 'read' ? (
                <Button size="sm" disabled={!readConfirmed || !documentOpened} onClick={() => setStep('confirm')}>
                  {t('document.ceremony.continue')}
                </Button>
              ) : (
                <Button size="sm" isLoading={isBusy} disabled={isBusy} onClick={() => void confirmAcknowledgment()}>
                  {t('document.ceremony.confirmAction')}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

export default AcknowledgmentCeremony;
